import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import type { KnowledgeDocument } from '../../api';
import { CodeDocumentPreview } from './CodeDocumentPreview';
import { DocxPreview } from './BinaryDocumentPreview';
import { MermaidDiagram } from './MermaidDiagram';
import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';

export type KnowledgePreviewDocument = Pick<
  KnowledgeDocument,
  'path' | 'title' | 'content'
>;

export const MarkdownPreview = ({
  document,
}: {
  document: KnowledgePreviewDocument;
}) => {
  const colors = useKnowledgeBuilderColors();

  const imageSrc = useMemo(
    () => imageSourceFor(document),
    [document],
  );

  const filePreview = useMemo(
    () => filePreviewFor(document),
    [document],
  );

  const apiPreview = useMemo(
    () => apiPreviewFor(document),
    [document],
  );

  const isMarkdown = isMarkdownDocument(document);

  const isCodePreview =
    !imageSrc &&
    !apiPreview &&
    !filePreview &&
    !isMarkdown;

  /*
   * Code files must not live inside the centered/document-style wrapper.
   *
   * Monaco requires a parent with an explicit usable height. Keeping it
   * directly under the full-height preview root lets automaticLayout size
   * the editor correctly.
   */
  if (isCodePreview) {
    return (
      <CodeDocumentPreview
        path={document.path}
        content={document.content}
      />
    );
  }

  return (
    <div
      className="knowledge-builder-scrollbar"
      style={{
        height: '100%',
        minHeight: 0,
        overflow: 'auto',
        boxSizing: 'border-box',
        background: colors.bg.panel,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 1080,
          margin: '0 auto',
          padding: '32px 40px 64px',
          boxSizing: 'border-box',
        }}
      >
        {imageSrc ? (
          <div style={{ marginTop: 16 }}>
            <img
              src={imageSrc}
              alt={document.title}
              style={{
                maxWidth: '100%',
                maxHeight: 560,
                objectFit: 'contain',
                border: `1px solid ${colors.border.default}`,
                borderRadius: 8,
                background: '#fff',
              }}
            />
          </div>
        ) : apiPreview ? (
          <ApiPreview preview={apiPreview} />
        ) : filePreview ? (
          <FilePreview
            document={document}
            preview={filePreview}
          />
        ) : (
          <MarkdownDocument content={document.content} />
        )}
      </div>
    </div>
  );
};

const MarkdownDocument = ({
  content,
}: {
  content: string;
}) => {
  const colors = useKnowledgeBuilderColors();

  return (
    <div>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <Typography
              variant="h4"
              component="h1"
              style={{
                marginTop: 0,
                marginBottom: 24,
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {children}
            </Typography>
          ),

          h2: ({ children }) => (
            <Typography
              variant="h5"
              component="h2"
              style={{
                marginTop: 36,
                marginBottom: 16,
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              {children}
            </Typography>
          ),

          h3: ({ children }) => (
            <Typography
              variant="h6"
              component="h3"
              style={{
                marginTop: 28,
                marginBottom: 12,
                fontWeight: 600,
              }}
            >
              {children}
            </Typography>
          ),

          h4: ({ children }) => (
            <Typography
              variant="subtitle1"
              component="h4"
              style={{
                marginTop: 24,
                marginBottom: 10,
                fontWeight: 600,
              }}
            >
              {children}
            </Typography>
          ),

          h5: ({ children }) => (
            <Typography
              variant="subtitle2"
              component="h5"
              style={{
                marginTop: 20,
                marginBottom: 8,
                fontWeight: 600,
              }}
            >
              {children}
            </Typography>
          ),

          h6: ({ children }) => (
            <Typography
              variant="subtitle2"
              component="h6"
              style={{
                marginTop: 18,
                marginBottom: 8,
                fontWeight: 600,
                color: colors.text.muted,
              }}
            >
              {children}
            </Typography>
          ),

          p: ({ children }) => (
            <Typography
              variant="body1"
              component="p"
              style={{
                marginTop: 0,
                marginBottom: 16,
                lineHeight: 1.72,
              }}
            >
              {children}
            </Typography>
          ),

          ul: ({ children }) => (
            <ul
              style={{
                marginTop: 8,
                marginBottom: 18,
                paddingLeft: 28,
                lineHeight: 1.65,
              }}
            >
              {children}
            </ul>
          ),

          ol: ({ children }) => (
            <ol
              style={{
                marginTop: 8,
                marginBottom: 18,
                paddingLeft: 28,
                lineHeight: 1.65,
              }}
            >
              {children}
            </ol>
          ),

          li: ({ children }) => (
            <li
              style={{
                marginBottom: 6,
                paddingLeft: 2,
              }}
            >
              {children}
            </li>
          ),

          blockquote: ({ children }) => (
            <div
              style={{
                margin: '20px 0',
                padding: '8px 16px',
                borderLeft: `4px solid ${colors.accent.blue}`,
                background: colors.bg.hover,
                color: colors.text.muted,
              }}
            >
              {children}
            </div>
          ),

          hr: () => (
            <div
              style={{
                height: 1,
                margin: '32px 0',
                background: colors.border.default,
              }}
            />
          ),

          a: ({ href, children }) => (
            <a
              href={href}
              target={isExternalHref(href) ? '_blank' : undefined}
              rel={
                isExternalHref(href)
                  ? 'noreferrer noopener'
                  : undefined
              }
              style={{
                color: colors.accent.blue,
                textDecoration: 'none',
              }}
            >
              {children}
            </a>
          ),

          strong: ({ children }) => (
            <strong style={{ fontWeight: 700 }}>
              {children}
            </strong>
          ),

          table: ({ children }) => (
            <TableContainer
              component={Paper}
              variant="outlined"
              style={{
                margin: '20px 0 28px',
                boxShadow: 'none',
                borderColor: colors.border.default,
              }}
            >
              <Table size="small">{children}</Table>
            </TableContainer>
          ),

          thead: ({ children }) => (
            <TableHead>{children}</TableHead>
          ),

          tbody: ({ children }) => (
            <TableBody>{children}</TableBody>
          ),

          tr: ({ children }) => (
            <TableRow>{children}</TableRow>
          ),

          th: ({ children }) => (
            <TableCell
              style={{
                fontWeight: 700,
                background: colors.bg.hover,
                borderBottomColor: colors.border.default,
                verticalAlign: 'top',
              }}
            >
              {children}
            </TableCell>
          ),

          td: ({ children }) => (
            <TableCell
              style={{
                borderBottomColor: colors.border.default,
                verticalAlign: 'top',
              }}
            >
              {children}
            </TableCell>
          ),

          pre: ({ children }) => {
            const child = Array.isArray(children)
              ? children[0]
              : children;

            const props =
              child &&
              typeof child === 'object' &&
              'props' in child
                ? (child as any).props
                : undefined;

            const className = props?.className ?? '';

            if (
              typeof className === 'string' &&
              className.includes('language-mermaid')
            ) {
              return (
                <div
                  style={{
                    margin: '24px 0',
                    border: `1px solid ${colors.border.default}`,
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <MermaidDiagram
                    code={String(
                      props.children ?? '',
                    ).replace(/\n$/, '')}
                  />
                </div>
              );
            }

            return (
              <pre
                style={{
                  margin: '20px 0',
                  padding: 16,
                  overflow: 'auto',
                  border: `1px solid ${colors.border.default}`,
                  borderRadius: 8,
                  background: colors.bg.hover,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {children}
              </pre>
            );
          },

          code: ({ className, children }) => {
            const isBlock = Boolean(className);

            if (isBlock) {
              return (
                <code
                  className={className}
                  style={{
                    fontFamily:
                      '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                  }}
                >
                  {children}
                </code>
              );
            }

            return (
              <code
                style={{
                  padding: '2px 5px',
                  borderRadius: 4,
                  background: colors.bg.hover,
                  fontFamily:
                    '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                  fontSize: '0.9em',
                }}
              >
                {children}
              </code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

const ApiPreview = ({
  preview,
}: {
  preview: ApiPreviewModel;
}) => (
  <div style={{ marginTop: 16, maxWidth: 1040 }}>
    <Typography variant="h6">
      {preview.title}
    </Typography>

    <Typography
      variant="body2"
      color="textSecondary"
    >
      {preview.kind} {preview.version}
      {preview.apiVersion
        ? ` · spec ${preview.apiVersion}`
        : ''}
    </Typography>

    {preview.description && (
      <Typography
        variant="body2"
        style={{
          marginTop: 12,
          maxWidth: 760,
        }}
      >
        {preview.description}
      </Typography>
    )}

    {preview.servers.length > 0 && (
      <PreviewSection
        title="Servers"
        items={preview.servers}
      />
    )}

    {preview.operations.length > 0 && (
      <div style={{ marginTop: 20 }}>
        <Typography variant="subtitle2">
          {preview.kind === 'AsyncAPI'
            ? 'Channels'
            : 'Operations'}
        </Typography>

        <div style={{ marginTop: 8 }}>
          {preview.operations.map(operation => (
            <div
              key={`${operation.method}-${operation.path}`}
              style={{
                border: '1px solid #e3e6eb',
                borderRadius: 8,
                padding: '10px 12px',
                marginBottom: 8,
              }}
            >
              <Typography variant="body2">
                <code>{operation.method}</code>{' '}
                <strong>{operation.path}</strong>
              </Typography>

              {operation.summary && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{
                    display: 'block',
                    marginTop: 4,
                  }}
                >
                  {operation.summary}
                </Typography>
              )}
            </div>
          ))}
        </div>
      </div>
    )}

    {preview.operations.length === 0 && (
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginTop: 20 }}
      >
        No operations or channels found.
      </Typography>
    )}
  </div>
);

const PreviewSection = ({
  title,
  items,
}: {
  title: string;
  items: string[];
}) => (
  <div style={{ marginTop: 20 }}>
    <Typography variant="subtitle2">
      {title}
    </Typography>

    <ul style={{ marginTop: 8 }}>
      {items.map(item => (
        <li key={item}>
          <Typography variant="body2">
            <code>{item}</code>
          </Typography>
        </li>
      ))}
    </ul>
  </div>
);

const FilePreview = ({
  document,
  preview,
}: {
  document: KnowledgePreviewDocument;
  preview: {
    kind: 'pdf' | 'docx' | 'office';
    label: string;
    src: string;
  };
}) => {
  if (preview.kind === 'pdf') {
    return (
      <iframe
        title={document.title}
        src={preview.src}
        style={{
          width: '100%',
          height: 620,
          marginTop: 16,
          border: '1px solid #e3e6eb',
          borderRadius: 8,
        }}
      />
    );
  }

  if (preview.kind === 'docx') {
    return (
      <div
        style={{
          marginTop: 16,
          height: 620,
          border: '1px solid #e3e6eb',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <DocxPreview
          document={document as KnowledgeDocument}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 16,
        padding: 16,
        border: '1px solid #e3e6eb',
        borderRadius: 8,
        maxWidth: 520,
      }}
    >
      <Typography variant="subtitle2">
        {document.title}
      </Typography>

      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginTop: 6 }}
      >
        {preview.label}
      </Typography>

      <a
        href={preview.src}
        download={document.path.split('/').pop()}
        style={{
          display: 'inline-block',
          marginTop: 12,
        }}
      >
        Download file
      </a>
    </div>
  );
};

function isMarkdownDocument(
  document: KnowledgePreviewDocument,
) {
  return /\.(md|mdx)$/i.test(document.path);
}

function isExternalHref(href?: string) {
  return Boolean(
    href && /^https?:\/\//i.test(href),
  );
}

function imageSourceFor(
  document: KnowledgePreviewDocument,
) {
  if (/\.png$/i.test(document.path)) {
    return document.content.startsWith(
      'data:image/png;base64,',
    )
      ? document.content
      : undefined;
  }

  if (/\.svg$/i.test(document.path)) {
    if (
      document.content.startsWith(
        'data:image/svg+xml',
      )
    ) {
      return document.content;
    }

    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      document.content,
    )}`;
  }

  return undefined;
}

function filePreviewFor(
  document: KnowledgePreviewDocument,
) {
  if (
    /\.pdf$/i.test(document.path) &&
    document.content.startsWith('data:')
  ) {
    return {
      kind: 'pdf' as const,
      label: 'PDF document',
      src: document.content,
    };
  }

  if (
    /\.docx$/i.test(document.path) &&
    document.content.startsWith('data:')
  ) {
    return {
      kind: 'docx' as const,
      label: officeLabel(document.path),
      src: document.content,
    };
  }

  if (
    /\.(docx?|xlsx?)$/i.test(document.path) &&
    document.content.startsWith('data:')
  ) {
    return {
      kind: 'office' as const,
      label: officeLabel(document.path),
      src: document.content,
    };
  }

  return undefined;
}

interface ApiPreviewModel {
  kind: 'OpenAPI' | 'AsyncAPI';
  apiVersion?: string;
  title: string;
  version: string;
  description?: string;
  servers: string[];
  operations: Array<{
    method: string;
    path: string;
    summary?: string;
  }>;
}

export function apiPreviewFor(
  document: KnowledgePreviewDocument,
) {
  if (!/\.(ya?ml|json)$/i.test(document.path)) {
    return undefined;
  }

  const parsed = parseStructuredApiDocument(
    document.content,
  );

  if (!parsed) {
    return undefined;
  }

  if (parsed.openapi || parsed.swagger) {
    return openApiPreview(parsed);
  }

  if (parsed.asyncapi) {
    return asyncApiPreview(parsed);
  }

  return undefined;
}

function openApiPreview(
  value: Record<string, any>,
): ApiPreviewModel {
  const info = objectValue(value.info);
  const paths = objectValue(value.paths);

  const servers = arrayValue(value.servers)
    .map(server => objectValue(server).url)
    .filter(isString);

  const methods = new Set([
    'get',
    'put',
    'post',
    'delete',
    'options',
    'head',
    'patch',
    'trace',
  ]);

  const operations = Object.entries(paths).flatMap(
    ([path, pathItem]) =>
      Object.entries(objectValue(pathItem))
        .filter(([method]) =>
          methods.has(method.toLowerCase()),
        )
        .map(([method, operation]) => ({
          method: method.toUpperCase(),
          path,
          summary:
            stringValue(
              objectValue(operation).summary,
            ) ||
            stringValue(
              objectValue(operation).operationId,
            ),
        })),
  );

  return {
    kind: 'OpenAPI',
    apiVersion:
      stringValue(value.openapi) ||
      stringValue(value.swagger),
    title:
      stringValue(info.title) ||
      'Untitled API',
    version:
      stringValue(info.version) ||
      'unknown',
    description:
      stringValue(info.description),
    servers,
    operations,
  };
}

function asyncApiPreview(
  value: Record<string, any>,
): ApiPreviewModel {
  const info = objectValue(value.info);
  const servers = objectValue(value.servers);
  const channels = objectValue(value.channels);

  const operations = Object.entries(
    channels,
  ).flatMap(([path, channel]) => {
    const channelObject =
      objectValue(channel);

    const explicitOperations =
      objectValue(value.operations);

    const channelOperations = [
      'publish',
      'subscribe',
    ]
      .filter(
        method =>
          channelObject[method],
      )
      .map(method => ({
        method: method.toUpperCase(),

        path,

        summary:
          stringValue(
            objectValue(
              channelObject[method],
            ).summary,
          ) ||
          stringValue(
            objectValue(
              channelObject[method],
            ).operationId,
          ),
      }));

    if (channelOperations.length) {
      return channelOperations;
    }

    return Object.entries(
      explicitOperations,
    )
      .filter(([, operation]) => {
        const channelRef =
          objectValue(operation).channel;

        return (
          objectValue(channelRef).$ref ===
            `#/channels/${path}` ||
          objectValue(channelRef).address ===
            path
        );
      })
      .map(
        ([operationId, operation]) => ({
          method:
            stringValue(
              objectValue(operation)
                .action,
            )?.toUpperCase() ||
            'OP',

          path,

          summary:
            stringValue(
              objectValue(operation)
                .summary,
            ) || operationId,
        }),
      );
  });

  return {
    kind: 'AsyncAPI',

    apiVersion:
      stringValue(value.asyncapi),

    title:
      stringValue(info.title) ||
      'Untitled API',

    version:
      stringValue(info.version) ||
      'unknown',

    description:
      stringValue(info.description),

    servers: Object.entries(
      servers,
    ).map(([name, server]) => {
      const serverObject =
        objectValue(server);

      return `${name}: ${
        stringValue(serverObject.url) ||
        'unknown'
      }`;
    }),

    operations,
  };
}

function parseStructuredApiDocument(
  content: string,
) {
  try {
    return JSON.parse(
      content,
    ) as Record<string, any>;
  } catch {
    return parseSimpleYaml(content);
  }
}

function parseSimpleYaml(
  content: string,
) {
  const lines = content.split('\n');

  const root: Record<string, any> = {};

  const stack: Array<{
    indent: number;
    value: Record<string, any>;
  }> = [
    {
      indent: -1,
      value: root,
    },
  ];

  for (const rawLine of lines) {
    const withoutComment =
      rawLine.replace(
        /\s+#.*$/,
        '',
      );

    if (
      !withoutComment.trim() ||
      withoutComment
        .trimStart()
        .startsWith('#')
    ) {
      continue;
    }

    const match =
      withoutComment.match(
        /^(\s*)([^:]+):(.*)$/,
      );

    if (!match) {
      continue;
    }

    const indent =
      match[1].length;

    const key = unquoteYaml(
      match[2].trim(),
    );

    const rawValue =
      match[3].trim();

    while (
      stack.length > 1 &&
      indent <=
        stack[
          stack.length - 1
        ].indent
    ) {
      stack.pop();
    }

    const parent =
      stack[
        stack.length - 1
      ].value;

    if (!rawValue) {
      const child: Record<
        string,
        any
      > = {};

      parent[key] = child;

      stack.push({
        indent,
        value: child,
      });
    } else {
      parent[key] =
        parseYamlScalar(
          rawValue,
        );
    }
  }

  return root;
}

function parseYamlScalar(
  value: string,
) {
  const trimmed = value.trim();

  if (/^\[.*\]$/.test(trimmed)) {
    return trimmed
      .slice(1, -1)
      .split(',')
      .map(item =>
        unquoteYaml(
          item.trim(),
        ),
      )
      .filter(Boolean);
  }

  return unquoteYaml(trimmed);
}

function unquoteYaml(
  value: string,
) {
  return value.replace(
    /^['"]|['"]$/g,
    '',
  );
}

function objectValue(
  value: any,
): Record<string, any> {
  return value &&
    typeof value === 'object' &&
    !Array.isArray(value)
    ? value
    : {};
}

function arrayValue(
  value: any,
): any[] {
  return Array.isArray(value)
    ? value
    : [];
}

function stringValue(
  value: any,
) {
  return typeof value === 'string'
    ? value
    : undefined;
}

function isString(
  value: any,
): value is string {
  return typeof value === 'string';
}

function officeLabel(
  path: string,
) {
  if (/\.docx?$/i.test(path)) {
    return 'Word document';
  }

  return 'Excel workbook';
}
